import React, { useState, useCallback, useEffect } from 'react';

import styles from '../QuickAddModal.module.css';

interface QuickAddTaskContentProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (task: NewTaskData) => void;
}

export interface NewTaskData {
  title: string;
  estimatedDuration: number;
  priority: 'low' | 'medium' | 'high';
  categoryId: string;
  notes: string;
}

const DURATION_OPTIONS = [
  { value: 15, label: '15m' },
  { value: 30, label: '30m' },
  { value: 45, label: '45m' },
  { value: 60, label: '1h' },
  { value: 120, label: '2h' },
];

const PRIORITY_OPTIONS = [
  { value: 'low' as const, label: 'Low', color: '#718096' },
  { value: 'medium' as const, label: 'Medium', color: '#ed8936' },
  { value: 'high' as const, label: 'High', color: '#e53e3e' },
];

const CATEGORIES = [
  { id: 'deep-work', name: 'Deep Work', color: '#2b6cb0' },
  { id: 'shallow', name: 'Shallow', color: '#9f7aea' },
  { id: 'personal', name: 'Personal', color: '#38a169' },
  { id: 'meeting', name: 'Meeting', color: '#ed8936' },
];

export function QuickAddTaskContent({ isOpen, onClose, onSave }: QuickAddTaskContentProps) {
  const [title, setTitle] = useState('');
  const [estimatedDuration, setEstimatedDuration] = useState(30);
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [categoryId, setCategoryId] = useState('deep-work');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setEstimatedDuration(30);
      setPriority('medium');
      setCategoryId('deep-work');
      setNotes('');
    }
  }, [isOpen]);

  const handleSave = useCallback(() => {
    if (!title.trim()) return;

    onSave({
      title: title.trim(),
      estimatedDuration,
      priority,
      categoryId,
      notes: notes.trim(),
    });

    onClose();
  }, [title, estimatedDuration, priority, categoryId, notes, onSave, onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'Enter' && e.metaKey) {
        handleSave();
      }
    },
    [onClose, handleSave]
  );

  return (
    <div className={`${styles.modal} ${styles.panelModal}`} onKeyDown={handleKeyDown}>
      <div className={styles.header}>
        <h2 id="quick-add-task-title" className={styles.title}>
          New Task
        </h2>
      </div>

      <div className={styles.body}>
        <div className={styles.formGroup}>
          <label htmlFor="task-title" className={styles.label}>
            Title
          </label>
          <input
            id="task-title"
            type="text"
            className={styles.input}
            placeholder="What needs to be done?"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Estimated Duration</label>
          <div className={styles.chipGroup}>
            {DURATION_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={`${styles.chip} ${estimatedDuration === opt.value ? styles.active : ''}`}
                onClick={() => setEstimatedDuration(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Priority</label>
          <div className={styles.chipGroup}>
            {PRIORITY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={`${styles.chip} ${priority === opt.value ? styles.active : ''}`}
                style={
                  priority === opt.value
                    ? {
                        borderColor: opt.color,
                        background: `${opt.color}20`,
                        color: opt.color,
                      }
                    : {}
                }
                onClick={() => setPriority(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Category</label>
          <div className={styles.chipGroup}>
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                className={`${styles.chip} ${styles.categoryChip} ${
                  categoryId === cat.id ? styles.active : ''
                }`}
                style={
                  categoryId === cat.id
                    ? {
                        borderColor: cat.color,
                        background: `${cat.color}20`,
                        color: cat.color,
                      }
                    : {}
                }
                onClick={() => setCategoryId(cat.id)}
              >
                <span
                  className={styles.categoryDot}
                  style={{ background: cat.color }}
                  aria-hidden="true"
                />
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="task-notes" className={styles.label}>
            Notes (optional)
          </label>
          <textarea
            id="task-notes"
            className={styles.textarea}
            placeholder="Add details, links, context..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>

      <div className={styles.footer}>
        <button className={styles.cancelBtn} onClick={onClose}>
          Cancel
        </button>
        <button className={styles.saveBtn} onClick={handleSave} disabled={!title.trim()}>
          Add Task
        </button>
      </div>
    </div>
  );
}

export default QuickAddTaskContent;
